import { useEffect, useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { Loader } from '@components';
import MovementCard from './MovementCard';
import WeekSelector from './WeekSelector';
import WeeklyPerformanceCard from './WeeklyPerformanceCard';
import { formatLocalDate } from './utils';
import {
  ChartListData,
  ChartData,
  WeeklyChangelogResult,
  CountryData,
  NetworkData,
  PlatformData,
  WeekFilter,
} from './types';
import { AD_IMPRESSIONS_CHART_TYPE, NETWORK_NAME, PLATFORM_NAME } from './constants';

const WeeklyChangelog: React.FC<{
  chartList: ChartListData | undefined;
  handleFilterChange: (key: string, selectedValue: string) => void;
  selections: Record<string, string>;
  weekFilter: WeekFilter[];
  fetchDashboardData: (
    dashboardData: ChartListData[],
    setChartData: React.Dispatch<ChartData[]>,
    setComponentLoader: React.Dispatch<React.SetStateAction<boolean>>,
    filters?: Record<string, string>,
  ) => Promise<void>;
}> = ({ chartList, handleFilterChange, selections, fetchDashboardData, weekFilter }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [startDate, setStartDate] = useState<string>('');
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(weekFilter.length - 2);

  const [componentLoader, setComponentLoader] = useState<boolean>(true);

  const weeklyChangelogData = chartData?.find(
    (chart: ChartData) =>
      chart.chartType === AD_IMPRESSIONS_CHART_TYPE.AD_IMPRESSIONS_WEEKLY_CHANGELOG,
  )?.result as WeeklyChangelogResult;
  const selectedWeekIndex = weeklyChangelogData?.data?.category_genre_data?.[0]?.current_week_index;
  const prevWeekIndex = weeklyChangelogData?.data?.category_genre_data?.[0]?.previous_week_index;
  const overallPercentageChange =
    weeklyChangelogData?.data?.category_genre_data?.[0]?.overall_percent_change;

  const totalWeeks = weekFilter.length - 1;

  const handlePrev = () => {
    setSelectedIndex((prev: number) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNext = () => {
    setSelectedIndex((prev: number) => (prev < totalWeeks - 1 ? prev + 1 : prev));
  };

  const countryData = weeklyChangelogData?.data?.country_data?.map((item: CountryData) => ({
    name: item.country,
    value: item.contribution_percentage,
    percentageChange: item.percent_change,
    weekAdSharePercent: item.week_ad_share_percent,
  }));

  const channelData = weeklyChangelogData?.data?.network_data?.map((item: NetworkData) => ({
    name: NETWORK_NAME[item.network as keyof typeof NETWORK_NAME],
    value: item.contribution_percentage,
    percentageChange: item.percent_change,
    weekAdSharePercent: item.week_ad_share_percent,
  }));

  const platformData = weeklyChangelogData?.data?.platform_data?.map((item: PlatformData) => ({
    name: PLATFORM_NAME[item.platform as keyof typeof PLATFORM_NAME],
    value: item.contribution_percentage,
    percentageChange: item.percent_change,
    weekAdSharePercent: item.week_ad_share_percent,
  }));

  const noData =
    (countryData?.length === 0 && channelData?.length === 0 && platformData?.length === 0) ||
    (countryData === undefined && channelData === undefined && platformData === undefined);

  useEffect(() => {
    if (selections.country && selections.category_genre && startDate) {
      fetchDashboardData(chartList ? [chartList] : [], setChartData, setComponentLoader, {
        category_genre: selections.category_genre,
        country: selections.country,
        start_date: startDate,
      });
    }
  }, [selections.country, startDate]);

  return (
    <Box
      sx={{
        width: '100%',
        height: 'fit-content',
        borderRadius: '1.25rem',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
        padding: '1rem',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: '1.375rem',
            color: '#162936',
          }}
        >
          Weekly Changelog
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Button
            onClick={handlePrev}
            disabled={selectedIndex === 0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              textTransform: 'none',
              color: selectedIndex === 0 ? '#51B8B050' : '#51B8B0',
            }}
          >
            <ArrowBackIosOutlinedIcon sx={{ height: '1rem', width: '1rem' }} />
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
              }}
            >
              Prev
            </Typography>
          </Button>
          <WeekSelector
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            onSelectWeek={(start, end) => {
              const startDate = formatLocalDate(start);
              setStartDate(startDate);
            }}
            weekFilter={weekFilter}
          />
          {/* <Autocomplete
            id="week-autocomplete"
            options={weekOptions}
            getOptionLabel={(option: any) => option.label}
            value={weekOptions[selectedIndex]}
            onChange={handleSelectChange}
            disableClearable
            renderInput={(params: any) => (
              <TextField
                {...params}
                placeholder="Week"
                fullWidth
                sx={{
                  width: isMobile ? "8rem" : "14rem",
                  height: "2.5rem",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  color: "#6C6C6C",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6C6C6C",
                  },
                  "& .MuiInputBase-root": {
                    height: "2.5rem",
                    display: "flex",
                    alignItems: "center",
                    paddingRight: "2rem",
                  },
                  "& input": {
                    padding: 0,
                  },
                }}
              />
            )}
          /> */}
          <Button
            onClick={handleNext}
            disabled={selectedIndex === totalWeeks - 1}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              textTransform: 'none',
              color: selectedIndex === totalWeeks - 1 ? '#51B8B050' : '#51B8B0',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
              }}
            >
              Next
            </Typography>
            <ArrowForwardIosOutlinedIcon sx={{ height: '1rem', width: '1rem' }} />
          </Button>
        </Box>
      </Box>
      {componentLoader ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '440px',
          }}
        >
          <Loader />
        </Box>
      ) : noData ? (
        <Box
          sx={{
            height: '440px',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          No data
        </Box>
      ) : (
        <>
          <WeeklyPerformanceCard
            selectedIndex={selectedIndex}
            selectedWeekIndex={selectedWeekIndex}
            prevWeekIndex={prevWeekIndex}
            overallPercentageChange={overallPercentageChange}
          />
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '0.75rem',
            }}
          >
            {countryData && (
              <MovementCard
                title="Top Movements across Countries"
                movementData={countryData}
                barColor="#008080"
                subTextTooltip="Wow % change in index for this country"
              />
            )}
            {channelData && (
              <MovementCard
                title="Top Movements across Channels"
                movementData={channelData}
                barColor="#5E6AAB"
                subTextTooltip="Wow % change in index for this channel"
              />
            )}
            {platformData && (
              <MovementCard
                title="Movements across Platform"
                movementData={platformData}
                barColor="#0072B2"
                subTextTooltip="Wow % change in index for this platform"
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WeeklyChangelog;
